import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Heart, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/#features', label: 'Features' },
    { to: '/#how', label: 'How it works' },
    { to: '/#pricing', label: 'Pricing' },
    { to: '/articles', label: 'Articles' },
    { to: '/#faq', label: 'FAQ' }
  ];

  const handleAnchor = (to) => {
    setOpen(false);
    if (to.startsWith('/#')) {
      const id = to.replace('/#', '');
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(to);
    }
  };

  const doLogout = async () => {
    await logout();
    setMenu(false);
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#FBF7F2]/85 backdrop-blur-md border-b border-[#EFE3D8]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-full bg-[#7B2C3B] flex items-center justify-center transition-transform group-hover:rotate-12">
            <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
          </div>
          <span className="font-serif text-xl font-semibold tracking-tight text-[#2A2A2A]">
            Yourwedding<span className="italic text-[#7B2C3B]">friends</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => handleAnchor(l.to)}
              className="text-sm text-[#3A3A3A] hover:text-[#7B2C3B] transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#7B2C3B] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setMenu(!menu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-[#F4E4DA] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#F4D6CE] flex items-center justify-center text-[#7B2C3B] font-serif font-semibold text-sm overflow-hidden">
                  {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.partner1 || user.email)[0].toUpperCase()}
                </div>
                <span className="text-sm text-[#2A2A2A]">{user.partner1 || user.email.split('@')[0]}</span>
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-52 soft-card rounded-2xl py-2 shadow-lg">
                  <button
                    onClick={() => { setMenu(false); navigate('/dashboard'); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-[#F4E4DA] flex items-center gap-2 text-[#2A2A2A]"
                  >
                    <LayoutDashboard className="w-4 h-4" /> My dashboard
                  </button>
                  <button
                    onClick={doLogout}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-[#F4E4DA] flex items-center gap-2 text-[#2A2A2A]"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button
                variant="ghost"
                className="text-sm text-[#3A3A3A] hover:text-[#7B2C3B] hover:bg-transparent"
                onClick={() => navigate('/auth')}
              >
                Sign in
              </Button>
              <button
                className="btn-primary text-sm font-medium px-5 py-2.5 rounded-full"
                onClick={() => handleAnchor('/#pricing')}
              >
                Start planning
              </button>
            </>
          )}
        </div>

        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2A2A2A]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FBF7F2] border-t border-[#EFE3D8] px-6 py-6 space-y-4">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => handleAnchor(l.to)}
              className="block w-full text-left text-base text-[#2A2A2A] py-2 border-b border-[#EFE3D8]"
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <button
                className="w-full btn-outline text-sm font-medium py-3 rounded-full"
                onClick={() => { setOpen(false); navigate('/dashboard'); }}
              >
                My dashboard
              </button>
              <button
                className="w-full btn-primary text-sm font-medium py-3 rounded-full"
                onClick={doLogout}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <button
                className="w-full btn-outline text-sm font-medium py-3 rounded-full"
                onClick={() => { setOpen(false); navigate('/auth'); }}
              >
                Sign in
              </button>
              <button
                className="w-full btn-primary text-sm font-medium py-3 rounded-full"
                onClick={() => handleAnchor('/#pricing')}
              >
                Start planning
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
